import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  BellRing,
  ChevronDown,
  ChevronUp,
  CircleHelp,
  Crown,
  GraduationCap,
  LogOut,
  Moon,
  Search,
  Settings,
  Sun,
  UserRound,
} from 'lucide-react';
import { useMaterial3Theme } from '../../contexts/Material3ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import './PremiumHeader.css';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import GoPremiumCTA from '../premium/GoPremiumCTA';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationPanel from '../notifications/NotificationPanel';
import { MD3Menu, MD3MenuDivider, MD3MenuItem } from '../Material3';

/**
 * A Material Design 3–compliant header for the premium experience.
 *
 * This component displays a header with a navigation button, a title,
 * optional breadcrumbs and a search field. It uses CSS classes for
 * all styling – there are **no** inline style objects or `title`
 * attributes that cause unwanted tooltips.
 *
 * Props:
 * - `title` (string): The page title to display.
 * - `breadcrumbs` (array of `{ label: string, href?: string }`): Optional
 *     breadcrumb links. If provided, the header will render an ordered
 *     list of links separated by chevrons. If `href` is omitted, the
 *     breadcrumb will render as plain text.
 */
export default function PremiumHeader({ title, breadcrumbs = [], onSearch }) {
  const navigate = useNavigate();
  const { actualTheme, toggleTheme } = useMaterial3Theme();
  const { user, logout } = useAuth();
  const [query, setQuery] = useState('');
  const { isPremium, openPremiumModal } = useEntitlements();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const { unreadCount } = useNotifications();
  const userMenuButtonRef = useRef(null);
  const notificationButtonRef = useRef(null);

  /**
   * Navigate to the dashboard. This replaces the inline `title`
   * attribute previously used to describe the button, using
   * `aria-label` instead to avoid native tooltips while remaining
   * accessible.
   */
  const handleLogoClick = () => navigate('/dashboard');

  /**
   * Handle changes to the search field. At some point you could
   * dispatch this query to a search endpoint or update context state.
   * For now it simply updates local state.
   */
  const handleSearchChange = (e) => setQuery(e.target.value);

  /**
   * Toggle the theme between light and dark modes
   */
  const handleThemeToggle = () => {
    toggleTheme();
  };

  /**
   * Toggle user menu dropdown
   */
  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  /**
   * Get user's initials for avatar fallback
   */
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    return user.name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  /**
   * Handle logout - clears HttpOnly cookies and navigates to login
   */
  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="premium-header">
      <div className="premium-header-left">
        <button
          type="button"
          className="premium-header-logo-btn"
          onClick={handleLogoClick}
          aria-label="Home"
        >
          <img
            src="/ShelfQuest_logo_v3.png"
            alt="ShelfQuest"
            className="header-logo-image"
            width="48"
            height="48"
          />
        </button>
        <h1 className="premium-header-title">{title}</h1>
        {breadcrumbs.length > 0 && (
          <nav aria-label="Breadcrumb" className="premium-header-breadcrumbs">
            <ol>
              {breadcrumbs.map((crumb, idx) => (
                <li key={idx} className="premium-header-crumb">
                  {crumb.href ? (
                    <a href={crumb.href}>{crumb.label}</a>
                  ) : (
                    <span>{crumb.label}</span>
                  )}
                  {idx < breadcrumbs.length - 1 && (
                    <span className="premium-header-crumb-separator">›</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}
      </div>
      <div className="premium-header-right">
        <div className="premium-header-search">
          <Search className="search-icon" size={20} aria-hidden="true" />
          <input
            type="text"
            className="premium-header-search-input"
            placeholder="Search…"
            aria-label="Search ShelfQuest"
            value={query}
            onChange={handleSearchChange}
          />
        </div>
        {/* Mobile search trigger (search field is hidden on mobile) */}
        <button
          type="button"
          className="premium-header-action-btn mobile-only"
          aria-label="Search"
          onClick={() => onSearch?.()}
        >
          <Search size={22} aria-hidden="true" />
        </button>
        {/* Inline Premium CTA for non-premium users */}
        {!isPremium && (
          <div className="premium-header-actions">
            <GoPremiumCTA to="/pricing" variant="tonal" className="premium-cta" />
          </div>
        )}

        {/* Action buttons */}
        <div className="premium-header-actions">
          {/* Theme Toggle */}
          <button
            type="button"
            className="premium-header-action-btn"
            onClick={handleThemeToggle}
            aria-label={`Switch to ${actualTheme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {actualTheme === 'dark'
              ? <Sun size={22} aria-hidden="true" />
              : <Moon size={22} aria-hidden="true" />}
          </button>

          {/* Notifications */}
          <div className="notification-bell-wrapper" style={{ position: 'relative' }}>
            <button
              type="button"
              ref={notificationButtonRef}
              className="premium-header-action-btn"
              aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
              aria-expanded={notifPanelOpen}
              aria-controls={notifPanelOpen ? 'notification-panel' : undefined}
              onClick={() => setNotifPanelOpen(prev => !prev)}
            >
              {unreadCount > 0
                ? <BellRing size={22} aria-hidden="true" />
                : <Bell size={22} aria-hidden="true" />}
              {unreadCount > 0 && (
                <span className="notification-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {notifPanelOpen && (
              <NotificationPanel
                onClose={() => setNotifPanelOpen(false)}
                returnFocusRef={notificationButtonRef}
              />
            )}
          </div>

          {/* User Menu */}
          <div className="premium-header-user-menu">
            <button
              type="button"
              id="user-menu-button"
              data-testid="user-menu"
              className="premium-header-user-btn"
              ref={userMenuButtonRef}
              onClick={toggleUserMenu}
              aria-label="User menu"
              aria-expanded={userMenuOpen}
              aria-haspopup="menu"
              aria-controls={userMenuOpen ? 'user-menu' : undefined}
            >
              <div className="user-avatar">
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.name} className="avatar-image" />
                ) : (
                  <span className="avatar-initials">{getUserInitials()}</span>
                )}
              </div>
              {userMenuOpen
                ? <ChevronUp className="dropdown-icon" size={18} aria-hidden="true" />
                : <ChevronDown className="dropdown-icon" size={18} aria-hidden="true" />}
            </button>

            {/* User Dropdown Menu */}
            <MD3Menu
              id="user-menu"
              open={userMenuOpen}
              onClose={() => setUserMenuOpen(false)}
              anchorEl={userMenuButtonRef.current}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              className="premium-header-dropdown"
              aria-labelledby="user-menu-button"
            >
              <div className="dropdown-header" role="presentation">
                <div className="user-info">
                  <span className="user-name">{user?.name || 'User'}</span>
                  <span className="user-email">{user?.email || 'user@example.com'}</span>
                </div>
              </div>
              {!isPremium && (
                <>
                  <MD3MenuDivider />
                  <MD3MenuItem
                    icon={<Crown size={20} aria-hidden="true" />}
                    onClick={() => {
                      openPremiumModal();
                      setUserMenuOpen(false);
                    }}
                  >
                    Go Premium
                  </MD3MenuItem>
                </>
              )}
              <MD3MenuDivider />
              <MD3MenuItem icon={<UserRound size={20} aria-hidden="true" />} onClick={() => { navigate('/profile'); setUserMenuOpen(false); }}>
                Profile
              </MD3MenuItem>
              <MD3MenuItem icon={<Settings size={20} aria-hidden="true" />} onClick={() => { navigate('/settings'); setUserMenuOpen(false); }}>
                Settings
              </MD3MenuItem>
              <MD3MenuItem icon={<Crown size={20} aria-hidden="true" />} onClick={() => { navigate('/pricing'); setUserMenuOpen(false); }}>
                Pricing
              </MD3MenuItem>
              <MD3MenuItem icon={<CircleHelp size={20} aria-hidden="true" />} onClick={() => { navigate('/help'); setUserMenuOpen(false); }}>
                Help
              </MD3MenuItem>
              <MD3MenuItem
                icon={<GraduationCap size={20} aria-hidden="true" />}
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('showTutorial'));
                  setUserMenuOpen(false);
                }}
              >
                Tutorial
              </MD3MenuItem>
              <MD3MenuDivider />
              <MD3MenuItem
                icon={<LogOut size={20} aria-hidden="true" />}
                className="logout-item md3-menu-item--destructive"
                data-testid="logout-button"
                onClick={handleLogout}
              >
                Sign Out
              </MD3MenuItem>
            </MD3Menu>
          </div>
        </div>
      </div>
    </header>
  );
}
